import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";
import { fmtAmount } from "@/lib/currency";
import { toast } from "sonner";

const fmt = (v, ccy) => fmtAmount(v, ccy || "USD");
const fmtDate = (d) => d ? new Date(d).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }) : "—";

const STATUS_CONFIG = {
  sent:          { label: "Awaiting Your Review",  cls: "bg-amber-100 text-amber-700",    icon: "Clock" },
  viewed:        { label: "Under Review",          cls: "bg-blue-100 text-blue-700",      icon: "Clock" },
  reminder_sent: { label: "Reminder Sent",         cls: "bg-orange-100 text-orange-700",  icon: "AlertCircle" },
  accepted:      { label: "Accepted",              cls: "bg-emerald-100 text-emerald-700",icon: "CheckCircle2" },
  declined:      { label: "Declined",              cls: "bg-slate-100 text-slate-500",    icon: "XCircle" },
  expired:       { label: "Expired",               cls: "bg-slate-100 text-slate-400",    icon: "XCircle" },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.viewed;
  const Ic = Icons[cfg.icon] || Icons.Clock;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm font-medium ${cfg.cls}`}
      data-testid="public-estimate-status-badge">
      <Ic className="h-3.5 w-3.5" />{cfg.label}
    </span>
  );
}

function LineItems({ items, currency }) {
  if (!items?.length) return null;
  return (
    <div className="border border-slate-100 rounded-xl overflow-hidden" data-testid="public-estimate-line-items">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-50 text-left text-xs text-slate-500 font-medium">
            <th className="px-4 py-3">Service</th>
            <th className="px-4 py-3 text-right">Qty</th>
            <th className="px-4 py-3 text-right">Amount</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, i) => (
            <tr key={item.id || i} className="border-t border-slate-100">
              <td className="px-4 py-3">
                <div className="font-medium text-slate-800">{item.name || item.description}</div>
                {item.description && item.name && item.description !== item.name && (
                  <div className="text-xs text-slate-400 mt-0.5">{item.description}</div>
                )}
              </td>
              <td className="px-4 py-3 text-right text-slate-600">{item.quantity ?? 1}</td>
              <td className="px-4 py-3 text-right font-medium text-slate-800">
                {fmt(item.amount ?? item.total ?? (item.unit_price * (item.quantity ?? 1)), currency)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Totals({ est }) {
  const ccy = est.currency || "USD";
  return (
    <div className="bg-slate-50 rounded-xl p-4 space-y-1.5" data-testid="public-estimate-totals">
      {est.subtotal != null && (
        <div className="flex justify-between text-sm text-slate-600">
          <span>Subtotal</span><span>{fmt(est.subtotal, ccy)}</span>
        </div>
      )}
      {est.discount > 0 && (
        <div className="flex justify-between text-sm text-emerald-600">
          <span>Discount</span><span>−{fmt(est.discount, ccy)}</span>
        </div>
      )}
      <div className="flex justify-between text-base font-bold text-slate-900 border-t border-slate-200 pt-2 mt-1">
        <span>Total</span><span>{fmt(est.total, ccy)}</span>
      </div>
      {est.payment_terms && (
        <p className="text-xs text-slate-400 pt-1 border-t border-slate-100 mt-1">
          Payment terms: {est.payment_terms}
        </p>
      )}
    </div>
  );
}

export default function PublicEstimate() {
  const { token } = useParams();
  const [est, setEst] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [accepted, setAccepted] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/sales/public/estimates/${token}`);
      setEst(data);
      if (data.status === "accepted") setAccepted(true);
    } catch {
      setError("This estimate link is invalid, has expired, or is no longer available.");
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const handleAccept = async () => {
    if (!agree) { toast.error("Please confirm you agree to the estimated charges."); return; }
    setBusy(true);
    try {
      await api.post(`/sales/public/estimates/${token}/accept`, {
        agree: true,
        acceptance_method: "email_link",
        accepted_by: est?.client?.full_name || "Client",
      });
      setAccepted(true);
      setEst((e) => ({ ...e, status: "accepted" }));
      toast.success("Estimate accepted — your tax team has been notified!");
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409) {
        setAccepted(true);
        setEst((e2) => ({ ...e2, status: "accepted" }));
        toast.info("This estimate has already been accepted.");
      } else {
        toast.error(typeof detail === "string" ? detail : "Could not accept estimate. Please try again.");
      }
    } finally { setBusy(false); }
  };

  const isActionable = est?.status && ["sent", "viewed", "reminder_sent"].includes(est.status) && !est.locked;
  const isExpiredOrDeclined = est?.status && ["expired", "declined"].includes(est.status);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="public-estimate-loading">
      <Icons.Loader2 className="h-6 w-6 animate-spin text-blue-600" />
    </div>
  );

  if (error || !est) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6" data-testid="public-estimate-error">
      <div className="text-center max-w-sm">
        <Icons.AlertTriangle className="h-10 w-10 text-amber-500 mx-auto mb-3" aria-hidden="true" />
        <p className="text-slate-600 text-sm">{error || "Estimate not found."}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4" data-testid="public-estimate-page">
      <div className="max-w-2xl mx-auto">

        {/* Brand header */}
        <div className="flex items-center gap-2 mb-6 justify-center">
          <div className="h-9 w-9 rounded-lg bg-blue-600 flex items-center justify-center">
            <Icons.Calculator className="h-5 w-5 text-white" aria-hidden="true" />
          </div>
          <span className="font-heading text-xl font-semibold text-slate-800">Expat Tax Firm</span>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">

          {/* Header strip */}
          <div className="bg-slate-900 text-white px-6 py-5">
            <div className="text-sm text-slate-300">Estimate</div>
            <h1 className="font-heading text-2xl font-semibold" data-testid="public-estimate-number">
              {est.estimate_number || "Estimate"}
            </h1>
            {est.title && <p className="text-slate-300 text-sm mt-1">{est.title}</p>}
          </div>

          <div className="p-6 space-y-5">

            {/* Status + expiry row */}
            <div className="flex flex-wrap items-center gap-3 justify-between">
              <StatusBadge status={est.status} />
              {est.expiration_date && (
                <span className="text-xs text-slate-400" data-testid="public-estimate-expiry">
                  Expires {fmtDate(est.expiration_date)}
                </span>
              )}
            </div>

            {/* Already-accepted state */}
            {(accepted || est.status === "accepted") && (
              <div className="flex items-start gap-3 bg-emerald-50 border border-emerald-200 rounded-xl p-5"
                data-testid="public-estimate-accepted-banner">
                <Icons.CheckCircle2 className="h-6 w-6 text-emerald-600 shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-emerald-800">You have accepted this estimate</div>
                  <p className="text-sm text-emerald-600 mt-0.5">
                    Thank you! Your tax team has been notified and will be in touch with next steps.
                  </p>
                </div>
              </div>
            )}

            {/* Expired / declined notice */}
            {isExpiredOrDeclined && !accepted && (
              <div className="flex items-start gap-3 bg-slate-50 border border-slate-200 rounded-xl p-4"
                data-testid="public-estimate-inactive-banner">
                <Icons.Info className="h-5 w-5 text-slate-400 shrink-0 mt-0.5" />
                <p className="text-sm text-slate-500">
                  This estimate is no longer available for acceptance. Please contact your tax team if you have questions.
                </p>
              </div>
            )}

            {/* Locked notice */}
            {est.locked && !accepted && (
              <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4"
                data-testid="public-estimate-locked-banner">
                <Icons.Lock className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                <p className="text-sm text-amber-700">
                  This estimate is currently being reviewed by your tax team. Please check back shortly.
                </p>
              </div>
            )}

            {/* Scope of services */}
            {(est.scope_summary || est.assumptions) && (
              <div className="space-y-3">
                <h2 className="font-semibold text-slate-800 text-sm uppercase tracking-wide text-slate-500">
                  Scope of Services
                </h2>
                {est.scope_summary && (
                  <p className="text-sm text-slate-600 leading-relaxed">{est.scope_summary}</p>
                )}
                {est.assumptions && (
                  <div className="bg-blue-50 rounded-lg p-3 text-xs text-blue-700">
                    <div className="font-medium mb-1">Assumptions</div>
                    <p className="leading-relaxed">{est.assumptions}</p>
                  </div>
                )}
              </div>
            )}

            {/* Line items + totals */}
            <div className="space-y-3">
              <h2 className="font-semibold text-slate-500 text-xs uppercase tracking-wide">
                Pricing Breakdown
              </h2>
              <LineItems items={est.line_items} currency={est.currency} />
              <Totals est={est} />
            </div>

            {/* Note from tax team */}
            {est.client_notes && (
              <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 text-sm text-amber-800"
                data-testid="public-estimate-notes">
                <div className="font-semibold mb-1">Note from Your Tax Team</div>
                <p className="leading-relaxed">{est.client_notes}</p>
              </div>
            )}

            {/* Accept section */}
            {isActionable && !accepted && (
              <div className="border-t border-slate-100 pt-5 space-y-4" data-testid="public-estimate-accept-section">
                <h2 className="font-semibold text-slate-800">Accept This Estimate</h2>
                <p className="text-sm text-slate-500">
                  Review the services and pricing above, then confirm your acceptance below.
                  Your tax team will be notified immediately.
                </p>
                <label className="flex items-start gap-3 bg-slate-50 rounded-xl p-4 cursor-pointer border border-slate-200 hover:border-slate-300 transition-colors">
                  <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)}
                    data-testid="public-estimate-agree" className="mt-0.5 accent-emerald-600 h-4 w-4" />
                  <span className="text-sm text-slate-700">
                    I have reviewed this estimate and agree to the services and charges as outlined above.
                  </span>
                </label>
                <button onClick={handleAccept} disabled={!agree || busy}
                  data-testid="public-estimate-accept-btn"
                  className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-semibold rounded-xl py-3 flex items-center justify-center gap-2 transition-colors">
                  {busy
                    ? <><Icons.Loader2 className="h-4 w-4 animate-spin" />Processing…</>
                    : <><Icons.Check className="h-4 w-4" />Accept Estimate</>}
                </button>
              </div>
            )}

          </div>
        </div>

        <p className="text-center text-xs text-slate-400 mt-4">
          Secured by Tax Flow · Expat Tax Firm
        </p>
      </div>
    </div>
  );
}
